<script setup>
import fiveMixin from '@/composables/fiveMixin';
import useFunctions from '@/composables/useFunctions';

const { ...five } = fiveMixin();
const { ...useFun } = useFunctions();

const props = defineProps({
  prBirthData: Object,
  prBirthJijanggan: Object,
  prLangMode: String,
  prBirthTitle: Array, 
  prPosition: String, 
});

function getYearSix(data, pos) {
  const posEng = getPosEng(pos);
  return data?.[posEng]?.six;
}

function getPosEng(pos) {
  if (pos === '년') return 'year';
  if (pos === '월') return 'month';
  if (pos === '일') return 'day';
  if (pos === '시') return 'hour';
  return '';
}

function getValueTxt(data, pos, val) {
  const posEng = getPosEng(pos);
  let txt = data?.[posEng]?.[val];
  if (val === 'sal') txt = txt ? txt[0] : '';
  return txt;
}

/** 격국 위치 데이터 분리 */
// 천간, 지지, 년월일시 '_'를 기준으로 분리 (앞 부분이 무조건 천간/지지 라는 보장 없어서)
function parseSajuPosition(position) {
  if (!position) return { char: '', type: '' };
  const [first, second] = position.split('_');
  const charMap = { yy: '년', mm: '월', dd: '일', ss: '시' };
  const typeMap = { cc: 'sky', gg: 'earth' };
  const char = charMap[first] ?? charMap[second] ?? '';
  const type = typeMap[first] ?? typeMap[second] ?? '';
  return { char, type };
}

/** 간지와 십성 페어링 */
// 간지 active시 십성도 active 하기 위해
function isActivePair(char, layerType) {
  const { char: activeChar, type: activeType } = parseSajuPosition(props.prPosition);
  return char === activeChar && layerType === activeType;
}

/** 격국 활성화 */
// prPosition에 따라 격국 위치의 간지 컬러 적용, 나머지 opacity 적용
function getOpacityClass(char, layerType) {
  const { char: activeChar, type: activeType } = parseSajuPosition(props.prPosition);

  if (layerType === 'sky' && char === '일') return 'active-opacity';

  if (char === activeChar && layerType === activeType) return 'active-opacity';

  return 'inactive-opacity';
}

/** 격국 border */
// 격국 위치 테두리 설정
function getBorderStyle(position) {
  const { char, type } = parseSajuPosition(position);

  if (!char || !type) return {};
  const posMap = {
    sky: {
      년: { top: '30px', left: '244px' },
      월: { top: '30px', left: '164px' },
      시: { top: '30px', left: '4px' },
    },
    earth: {
      년: { top: '116px', left: '244px' },
      월: { top: '116px', left: '164px' },
      일: { top: '116px', left: '84px' },
      시: { top: '116px', left: '4px' },
    },
  };

  return posMap[type]?.[char] ?? {};
}

</script>

<template>
  <div class="saju-summary__eight">
    <ul v-for="(char, index) in prBirthTitle" :key="index" class="birth-item-box birth txt-big">
      <li><span class="txt1">{{ char }}</span></li>

      <li>
        <span
          class="txts"
          :class="char === '일' || isActivePair(char, 'sky') ? 'active-opacity' : 'inactive-opacity'"
        >
          {{ getYearSix(prBirthData?.sky, char) }} {{ char === '일' ? '(나)' : '' }}
        </span>
      </li>

      <li>
        <span
          class="txt-round xl"
          :class="[
            five.getColorFive(getValueTxt(prBirthData?.sky, char, 'value')),
            props.prLangMode === 'CHNKOR' ? 'chnkor' : '',
            props.prLangMode,
            getOpacityClass(char, 'sky')
          ]"
        >
          <em v-if="props.prLangMode === 'CHNKOR'" class="chn">
            {{ five.getHanjaTxt(getValueTxt(prBirthData?.sky, char, 'value'), 'sky') }}
          </em>
          <em v-if="getValueTxt(prBirthData?.sky, char, 'value') === '-'" class="time-none">
            {{ getValueTxt(prBirthData?.sky, char, 'value') }}
          </em>
          <em v-else class="txt-main">
            {{ getValueTxt(prBirthData?.sky, char, 'value') }}
          </em>
        </span>
      </li>

      <li>
        <span
          class="txt-round xl mb6"
          :class="[
            five.getColorFive(getValueTxt(prBirthData?.earth, char, 'value')),
            props.prLangMode === 'CHNKOR' ? 'chnkor' : '',
            props.prLangMode,
            getOpacityClass(char, 'earth')
          ]"
        >
          <em v-if="props.prLangMode === 'CHNKOR'" class="chn">
            {{ five.getHanjaTxt(getValueTxt(prBirthData?.earth, char, 'value'), 'earth') }}
          </em>
          <em v-if="getValueTxt(prBirthData?.earth, char, 'value') === '-'" class="time-none">
            {{ getValueTxt(prBirthData?.earth, char, 'value') }}
          </em>
          <em v-else class="txt-main">
            {{ getValueTxt(prBirthData?.earth, char, 'value') }}
          </em>
        </span>
      </li>

      <li>
        <span
          class="txts"
          :class="isActivePair(char, 'earth') ? 'active-opacity' : 'inactive-opacity'"
        >
          {{ getYearSix(prBirthData?.earth, char) }}
        </span>
      </li>
    </ul>

    <div
      v-if="!(parseSajuPosition(props.prPosition).char === '일' && parseSajuPosition(props.prPosition).type === 'sky')"
      class="gg-active-border"
      :style="getBorderStyle(props.prPosition)"
    ></div>
  </div>
</template>

<style lang="scss" scoped>
.gg-active-border{
  width: 62px;
  height: 86px;
  position: absolute;
  border-radius: 16px;
  border: 1px solid var(--Orange-200, #F80);
  background: rgba(255, 136, 0, 0.03);
  box-shadow: 0 0 10px 0 rgba(255, 136, 0, 0.30);
}
</style>
